// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: adding, renaming and removing a machine's projects from Konvo (konvo-agents 0.22.0+):
// folders on the machine to add one from and GitHub repositories its own `gh` can clone. The pure
// helpers for Choose project are in AgentProjectsModel.ts.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {guessHome} from '@app/features/konvo_agents/state/AgentProjectsModel';
import Agents from '@app/features/konvo_agents/state/Agents';
import {http} from '@app/features/platform/transport/RestTransport';
import type {
	AgentAddedProject,
	AgentAddProject,
	AgentFolders,
	AgentProject,
	AgentRepos,
} from '@konvo/schema/src/domains/konvo/Agents';
import {runInAction} from 'mobx';

/** The machine's home folder, as its last folder listing said (paths show as ~/… with it). */
const homes = new Map<string, string>();
export const homeOf = (machineId: string) =>
	homes.get(machineId) ?? guessHome(Agents.projectsOf(machineId).map((project) => project.path));

/** A machine read kept for a while: when it was asked, the request, and its answer once in. */
interface Kept<T> {
	at: number;
	request: Promise<T>;
	value: T | null;
}

/** Each folder open is a round trip to the machine: lately listed folders open at once. */
const FOLDERS_TTL_MS = 60_000;
const MAX_FOLDER_READS = 200;
const folderReads = new Map<string, Kept<AgentFolders>>();
const folderKey = (machineId: string, path?: string) => `${machineId}\u0000${path ?? ''}`;

/** `gh repo list` on the machine takes seconds: kept per machine for Clone from GitHub. */
const REPOS_TTL_MS = 5 * 60_000;
const repoReads = new Map<string, Kept<AgentRepos>>();

const fresh = <T>(kept: Kept<T> | undefined, ttl: number): kept is Kept<T> =>
	kept != null && Date.now() - kept.at < ttl;

function keep<T>(
	reads: Map<string, Kept<T>>,
	key: string,
	request: Promise<T>,
	onValue?: (value: T, entry: Kept<T>) => void,
) {
	const entry: Kept<T> = {at: Date.now(), request, value: null};
	reads.set(key, entry);
	request.then(
		(value) => {
			entry.value = value;
			onValue?.(value, entry);
		},
		() => {
			if (reads.get(key) === entry) reads.delete(key);
		},
	);
	return entry;
}

/** The folders in `path` (left out: home), from a recent listing when there is one. */
export function loadFolders(machineId: string, path?: string): Promise<AgentFolders> {
	const key = folderKey(machineId, path);
	const kept = folderReads.get(key);
	if (fresh(kept, FOLDERS_TTL_MS)) return kept.request;
	if (folderReads.size >= MAX_FOLDER_READS) folderReads.delete(folderReads.keys().next().value!);
	const request = http
		.get<AgentFolders>(Endpoints.KONVO_AGENTS_FOLDERS(machineId), {query: path ? {path} : {}})
		.then((response) => response.body);
	return keep(folderReads, key, request, (listing, entry) => {
		homes.set(machineId, listing.home);
		// Also under the path the machine answered with (~/code and /home/jp/code are one folder).
		if (folderReads.get(key) === entry) folderReads.set(folderKey(machineId, listing.path), entry);
	}).request;
}

/** A recent listing of `path`, if one is in: shown at once instead of waiting for the machine. */
export function keptFolders(machineId: string, path?: string): AgentFolders | null {
	const kept = folderReads.get(folderKey(machineId, path));
	return fresh(kept, FOLDERS_TTL_MS) ? kept.value : null;
}

/** Lists a folder ahead of time (the pointer rests on it), so opening it is instant. */
export function prefetchFolders(machineId: string, path: string): void {
	loadFolders(machineId, path).catch(() => {});
}

function forgetFolders(machineId: string) {
	for (const key of [...folderReads.keys()]) if (key.startsWith(`${machineId}\u0000`)) folderReads.delete(key);
}

export function loadRepos(machineId: string): Promise<AgentRepos> {
	const kept = repoReads.get(machineId);
	if (fresh(kept, REPOS_TTL_MS)) return kept.request;
	const request = http.get<AgentRepos>(Endpoints.KONVO_AGENTS_REPOS(machineId)).then((response) => response.body);
	return keep(repoReads, machineId, request).request;
}

/** The repository list, if one was read lately. */
export function keptRepos(machineId: string): AgentRepos | null {
	const kept = repoReads.get(machineId);
	return fresh(kept, REPOS_TTL_MS) ? kept.value : null;
}

/** Puts a change into the machine's projects at once; the machine's own update follows. */
function patchProjects(machineId: string, change: (projects: Array<AgentProject>) => Array<AgentProject>) {
	runInAction(() => {
		Agents.machines = Agents.machines.map((machine) =>
			machine.id === machineId ? {...machine, projects: change(machine.projects)} : machine,
		);
		const list = Agents.lists.get(machineId);
		if (list) Agents.lists.set(machineId, {...list, projects: change(list.projects)});
	});
}

export async function addProject(machineId: string, add: AgentAddProject): Promise<AgentAddedProject> {
	const response = await http.post<AgentAddedProject>(Endpoints.KONVO_AGENTS_PROJECTS(machineId), {body: add});
	const added = response.body;
	// Its folder (and a new or cloned one's parent) lists differently now.
	forgetFolders(machineId);
	patchProjects(machineId, (projects) =>
		projects.some((p) => p.id === added.projectId)
			? projects
			: [...projects, {id: added.projectId, title: added.title, path: added.path}],
	);
	void Agents.loadOverview();
	return added;
}

export async function renameProject(machineId: string, projectId: string, title: string): Promise<void> {
	await http.patch(Endpoints.KONVO_AGENTS_PROJECT(machineId, projectId), {body: {title}});
	patchProjects(machineId, (projects) => projects.map((p) => (p.id === projectId ? {...p, title} : p)));
	void Agents.loadOverview();
}

/** Removes the project with its chats; the folder stays on the machine. */
export async function removeProject(machineId: string, projectId: string): Promise<{chats: number}> {
	const response = await http.delete<{accepted: true; chats: number}>(
		Endpoints.KONVO_AGENTS_PROJECT(machineId, projectId),
	);
	forgetFolders(machineId);
	patchProjects(machineId, (projects) => projects.filter((p) => p.id !== projectId));
	runInAction(() => {
		const list = Agents.lists.get(machineId);
		if (list?.threads)
			Agents.lists.set(machineId, {...list, threads: list.threads.filter((t) => t.projectId !== projectId)});
	});
	void Agents.loadOverview();
	return {chats: response.body.chats};
}
